import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, Mail, Bot, Plug, FileText, User, CheckCircle2, Sparkles,
  Plane, Hotel, Car, FileCheck, CalendarDays, Zap, ShieldCheck,
  Check, X, Bus,
} from "lucide-react";
import SEO from "../components/SEO";
import TasFlowFeatures from "../sections/TasFlowFeatures";
import TasFlowStory from "../sections/TasFlowStory";
import TasFlowFaz3 from "../sections/TasFlowFaz3";
import { useLang } from "../i18n/LanguageContext";
import { TASFLOW_I18N } from "../i18n/tasflow";

function Eyebrow({ children }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-[#F97316]/40 bg-[#F97316]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FB923C]">
      {children}
    </span>
  );
}

const HERO_ICONS = [Mail, Bot, Plug, FileText, User, CheckCircle2];
const SERVICE_ICONS = [Plane, Hotel, Car, FileCheck, CalendarDays, Bus];
const MODE_ICONS = [ShieldCheck, Zap];
const AIRLINE_KEYS = ["thy", "pegasus", "ajet", "sunexpress", "lufthansa"];
const AIRLINE_LOGOS = ["", "", "", "", ""];

const rise = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
};

export default function TasFlow() {
  const { lang } = useLang();
  const t = TASFLOW_I18N[lang];

  return (
    <div className="bg-[#07111F] text-white scroll-smooth" data-testid="tasflow-page">
      <SEO title={t.seo.title} description={t.seo.description} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "TAS Flow",
            alternateName: "Travel Automation System",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web, Cloud",
            inLanguage: lang,
            description: t.seo.schemaDesc,
            offers: t.packages.map((p) => ({
              "@type": "Offer",
              name: p.name,
              category: `${p.featureCount} ${t.seo.featureUnit}`,
            })),
            brand: { "@type": "Brand", name: "Dijital Roket" },
            publisher: { "@type": "Organization", name: "Dijital Roket", url: "https://www.dijitalroket.com" },
          }),
        }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-28 pb-24 sm:pt-32 sm:pb-32">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid opacity-25" />
          <div className="absolute -top-32 left-1/4 h-[460px] w-[460px] rounded-full bg-[#F97316]/25 blur-[140px] animate-orb-move" />
          <div className="absolute top-24 right-1/4 h-[420px] w-[420px] rounded-full bg-[#22D3EE]/20 blur-[140px] animate-orb-move" style={{ animationDelay: "4s" }} />
        </div>
        <div className="container-x relative text-center max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <img
              src="/tasflow-logo-white.png"
              alt={t.hero.logoAlt}
              data-testid="tasflow-hero-logo"
              className="mx-auto h-16 sm:h-20 w-auto"
            />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 font-heading text-4xl sm:text-6xl font-extrabold tracking-tight leading-[1.05]"
            data-testid="tasflow-hero-title"
          >
            {t.hero.titleTop}<br />
            <span className="bg-gradient-to-r from-[#F97316] via-[#FB923C] to-[#22D3EE] bg-clip-text text-transparent">{t.hero.titleAccent}</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-6 mx-auto max-w-2xl text-base sm:text-lg text-white/65 leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-9 flex flex-col sm:flex-row justify-center gap-3"
          >
            <a href="#demo" data-testid="tasflow-hero-demo" className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#F97316] to-[#FB923C] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#F97316]/30 transition hover:-translate-y-0.5">
              {t.hero.ctaDemo} <ArrowRight className="h-4 w-4" />
            </a>
            <a href="#ozellikler" data-testid="tasflow-hero-features" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-7 py-3.5 text-sm font-semibold text-white/80 hover:text-white hover:border-white/40 transition">
              {t.hero.ctaFeatures}
            </a>
          </motion.div>

          {/* animated flow */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.35 }}
            className="mt-14 flex flex-wrap items-center justify-center gap-2 sm:gap-3"
            data-testid="tasflow-hero-flow"
          >
            {t.hero.flow.map((label, i) => {
              const Icon = HERO_ICONS[i];
              return (
                <div key={label} className="flex items-center gap-2 sm:gap-3">
                  <motion.div
                    animate={{ y: [0, -6, 0] }}
                    transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.25, ease: "easeInOut" }}
                    className="flex flex-col items-center gap-1.5 rounded-2xl border border-white/10 bg-white/[0.04] px-3.5 py-3 backdrop-blur-xl min-w-[78px]"
                  >
                    <Icon className="h-4 w-4 text-[#FB923C]" />
                    <span className="text-[11px] font-medium text-white/70">{label}</span>
                  </motion.div>
                  {i < t.hero.flow.length - 1 && <span className="text-[#F97316]/60">→</span>}
                </div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* GROW NOT WORKLOAD */}
      <section className="border-y border-white/5 bg-[#020617] py-20">
        <div className="container-x text-center">
          <motion.h2 {...rise} transition={{ duration: 0.6 }} className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight">
            {t.grow.line1}<br /><span className="text-white/40">{t.grow.line2}</span>
          </motion.h2>
          <motion.p {...rise} transition={{ duration: 0.6, delay: 0.1 }} className="mt-6 mx-auto max-w-2xl text-white/60">
            {t.grow.sub} <span className="text-[#FB923C] font-semibold">{t.grow.subAccent}</span>
          </motion.p>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-24">
        <div className="container-x">
          <motion.h2 {...rise} transition={{ duration: 0.6 }} className="max-w-3xl font-heading text-3xl sm:text-4xl font-bold">
            {t.problem.title} <span className="text-[#FB923C]">{t.problem.titleAccent}</span>
          </motion.h2>
          <div className="mt-10 flex flex-wrap gap-2.5">
            {t.problem.steps.map((s, i) => (
              <motion.span
                key={s} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-white/60"
              >
                {s}
              </motion.span>
            ))}
          </div>
          <motion.p {...rise} transition={{ duration: 0.6 }} className="mt-12 font-heading text-2xl sm:text-3xl font-bold text-white">
            {t.problem.close} <span className="text-[#FB923C]">{t.problem.closeAccent}</span>
          </motion.p>
        </div>
      </section>

      {/* REQUEST → QUOTE */}
      <section className="relative bg-[#020617] py-24">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="container-x relative">
          <motion.div {...rise} transition={{ duration: 0.6 }} className="max-w-2xl">
            <Eyebrow>{t.quote.eyebrow}</Eyebrow>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold">{t.quote.title}</h2>
            <p className="mt-4 text-white/60">{t.quote.desc}</p>
          </motion.div>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {t.quote.steps.map((s, i) => (
              <motion.div key={s.n} {...rise} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur">
                <div className="font-heading text-3xl font-extrabold text-[#F97316]/30">{s.n}</div>
                <h3 className="mt-3 font-heading text-lg font-semibold text-white">{s.t}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AUTO RESERVATION */}
      <section className="py-24">
        <div className="container-x">
          <motion.div {...rise} transition={{ duration: 0.6 }} className="max-w-3xl">
            <Eyebrow>{t.autoRes.eyebrow}</Eyebrow>
            <h2 className="mt-4 font-heading text-3xl sm:text-5xl font-extrabold tracking-tight">
              {t.autoRes.titleTop}<br /><span className="bg-gradient-to-r from-[#F97316] to-[#FB923C] bg-clip-text text-transparent">{t.autoRes.titleAccent}</span>
            </h2>
            <p className="mt-5 text-white/60 max-w-2xl">{t.autoRes.desc}</p>
          </motion.div>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {t.autoRes.modes.map((m, i) => {
              const Icon = MODE_ICONS[i];
              return (
                <motion.div key={m.t} {...rise} transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#F97316]/30 to-[#FB923C]/20 border border-white/10">
                    <Icon className="h-5 w-5 text-[#FB923C]" />
                  </span>
                  <h3 className="mt-5 font-heading text-xl font-bold text-white">{m.t}</h3>
                  <p className="mt-2 text-white/55 leading-relaxed">{m.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PER-CUSTOMER WORKFLOW */}
      <section className="relative bg-[#020617] py-24">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="container-x relative">
          <motion.h2 {...rise} transition={{ duration: 0.6 }} className="max-w-3xl font-heading text-3xl sm:text-4xl font-bold">
            {t.perCustomer.title}<br /><span className="text-[#FB923C]">{t.perCustomer.titleAccent}</span>
          </motion.h2>
          <p className="mt-4 max-w-2xl text-white/60">{t.perCustomer.desc}</p>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {t.perCustomer.customers.map((c, i) => (
              <motion.div key={c.c} {...rise} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur">
                <div className="text-xs font-bold uppercase tracking-[0.16em] text-[#22D3EE]">{c.c}</div>
                <div className="mt-2 font-heading text-lg font-semibold text-white">{c.w}</div>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{c.d}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {t.perCustomer.tags.map((tag) => (
              <span key={tag} className="rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-xs text-white/55">{tag}</span>
            ))}
          </div>
        </div>
      </section>

      {/* AI & AUTOMATION */}
      <section id="ai" className="py-24 scroll-mt-24">
        <div className="container-x">
          <motion.div {...rise} transition={{ duration: 0.6 }} className="max-w-2xl">
            <Eyebrow><Sparkles className="h-3 w-3" /> {t.ai.eyebrow}</Eyebrow>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold">{t.ai.title}</h2>
          </motion.div>
          <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {t.ai.caps.map((a, i) => (
              <motion.div key={a} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: (i % 3) * 0.05 }}
                className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#FB923C]" />
                <span className="text-sm text-white/75">{a}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TRAVEL SERVICES */}
      <section className="relative bg-[#020617] py-24">
        <div className="container-x">
          <motion.div {...rise} transition={{ duration: 0.6 }} className="max-w-2xl">
            <Eyebrow>{t.services.eyebrow}</Eyebrow>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold">{t.services.title}</h2>
            <p className="mt-4 text-white/60">{t.services.desc}</p>
          </motion.div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {t.services.items.map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <motion.div key={s.t} {...rise} transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#F97316]/25 to-[#22D3EE]/20 border border-white/10">
                    <Icon className="h-5 w-5 text-[#FB923C]" strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-white">{s.t}</h3>
                  <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AIRLINES */}
      <section className="py-24" data-testid="tasflow-airlines">
        <div className="container-x">
          <motion.div {...rise} transition={{ duration: 0.6 }} className="max-w-2xl">
            <Eyebrow><Plane className="h-3 w-3" /> {t.airlines.eyebrow}</Eyebrow>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold">
              {t.airlines.title} <span className="bg-gradient-to-r from-[#F97316] to-[#FB923C] bg-clip-text text-transparent">{t.airlines.titleAccent}</span>
            </h2>
            <p className="mt-4 text-white/60">{t.airlines.desc}</p>
          </motion.div>

          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {t.airlines.names.map((name, i) => (
              <motion.div
                key={AIRLINE_KEYS[i]}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: (i % 5) * 0.06 }}
                data-testid={`tasflow-airline-${AIRLINE_KEYS[i]}`}
                className="group flex h-28 flex-col items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 text-center backdrop-blur transition hover:border-[#F97316]/40 hover:bg-white/[0.05]"
              >
                {AIRLINE_LOGOS[i] ? (
                  <img src={AIRLINE_LOGOS[i]} alt={name} className="max-h-12 w-auto object-contain" />
                ) : (
                  <>
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#F97316]/25 to-[#22D3EE]/15 border border-white/10">
                      <Plane className="h-4 w-4 text-[#FB923C]" strokeWidth={1.8} />
                    </span>
                    <span className="font-heading text-sm font-bold text-white leading-tight">{name}</span>
                  </>
                )}
              </motion.div>
            ))}
          </div>
          <p className="mt-6 text-xs text-white/35">{t.airlines.note}</p>
        </div>
      </section>

      {/* CORE DIFFERENCE */}
      <section className="py-24">
        <div className="container-x">
          <motion.h2 {...rise} transition={{ duration: 0.6 }} className="text-center font-heading text-3xl sm:text-4xl font-bold">
            {t.diff.title}
          </motion.h2>
          <div className="mt-12 grid gap-6 md:grid-cols-2 max-w-4xl mx-auto">
            <motion.div {...rise} transition={{ duration: 0.5 }} className="rounded-3xl border border-white/10 bg-white/[0.02] p-8">
              <div className="flex items-center gap-2 text-white/40"><X className="h-5 w-5" /> {t.diff.negLabel}</div>
              <p className="mt-4 font-heading text-xl font-semibold text-white/70">{t.diff.negText}</p>
            </motion.div>
            <motion.div {...rise} transition={{ duration: 0.5, delay: 0.1 }} className="rounded-3xl border border-[#F97316]/30 bg-gradient-to-br from-[#F97316]/10 to-transparent p-8">
              <div className="flex items-center gap-2 text-[#FB923C]"><Check className="h-5 w-5" /> TAS Flow</div>
              <p className="mt-4 font-heading text-xl font-semibold text-white">{t.diff.posText}</p>
              <ul className="mt-5 space-y-2 text-sm text-white/70">
                {t.diff.posList.map((x) => (
                  <li key={x} className="flex items-center gap-2"><Check className="h-4 w-4 text-[#FB923C]" /> {x}</li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SCROLL STORYTELLING */}
      <TasFlowStory />

      {/* PACKAGES + FEATURE TABLE */}
      <TasFlowFeatures />

      {/* ROI + FAQ + DEMO FORM */}
      <TasFlowFaz3 />

      {/* CLOSING CTA */}
      <section id="iletisim-cta" className="relative overflow-hidden bg-[#020617] py-24 scroll-mt-24">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid opacity-20" />
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-[360px] w-[520px] rounded-full bg-[#F97316]/20 blur-[130px]" />
        </div>
        <div className="container-x relative text-center max-w-3xl mx-auto">
          <motion.h2 {...rise} transition={{ duration: 0.6 }} className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight">
            {t.closing.title}
          </motion.h2>
          <motion.p {...rise} transition={{ duration: 0.6, delay: 0.1 }} className="mt-5 text-white/60">
            {t.closing.desc}
          </motion.p>
          <motion.div {...rise} transition={{ duration: 0.6, delay: 0.2 }} className="mt-9 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/proje-talep" data-testid="tasflow-cta-demo" className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#F97316] to-[#FB923C] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#F97316]/30 transition hover:-translate-y-0.5">
              {t.closing.ctaDemo} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/iletisim" data-testid="tasflow-cta-contact" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-7 py-3.5 text-sm font-semibold text-white/80 hover:text-white hover:border-white/40 transition">
              {t.closing.ctaContact}
            </Link>
          </motion.div>
          <p className="mt-6 text-xs text-white/35">{t.closing.note}</p>
        </div>
      </section>
    </div>
  );
}
